import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { Upload, ExternalLink, CheckCircle, Clock, Trash2, RefreshCw, Presentation } from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  submitted: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  live: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
};

const PLATFORM_OPTIONS = [
  { id: 'slideshare', label: 'SlideShare', traffic: 'High' },
  { id: 'speakerdeck', label: 'Speaker Deck', traffic: 'Medium' },
  { id: 'scribd', label: 'Scribd', traffic: 'High' },
  { id: 'issuu', label: 'Issuu', traffic: 'Medium' },
  { id: 'authorstream', label: 'AuthorStream', traffic: 'Low' },
];

export default function PPTSubmissionsPage() {
  const { selectedProjectId } = useProject();
  const [activeTab, setActiveTab] = useState('create');
  const [submissions, setSubmissions] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [keywords, setKeywords] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['slideshare', 'speakerdeck']);

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const loadSubmissions = async () => {
    try {
      const { data } = await api.get('/ppt-submissions/');
      setSubmissions(data.submissions || []);
    } catch {
      // silent
    }
  };

  const loadPlatforms = async () => {
    try {
      const { data } = await api.get('/ppt-submissions/platforms');
      setPlatforms(data.platforms || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    loadSubmissions();
    loadPlatforms();
  }, []);

  const togglePlatform = (id) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (selectedPlatforms.length === 0) {
      setStatus('Error: Select at least one platform.');
      return;
    }
    setLoading(true);
    setStatus('Creating submission...');
    try {
      await api.post('/ppt-submissions/', {
        title,
        description,
        file_url: fileUrl || null,
        website_url: websiteUrl || null,
        keywords: keywords.split(',').map((k) => k.trim()).filter(Boolean),
        platforms: selectedPlatforms,
        project_id: selectedProjectId || undefined,
      });
      setStatus('Submission created successfully!');
      setTitle('');
      setDescription('');
      setFileUrl('');
      setWebsiteUrl('');
      setKeywords('');
      setSelectedPlatforms(['slideshare', 'speakerdeck']);
      await loadSubmissions();
      setActiveTab('submissions');
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const markPlatform = async (submissionId, platform, newStatus) => {
    try {
      await api.patch(
        `/ppt-submissions/${submissionId}/platform/${platform}?status=${newStatus}`
      );
      await loadSubmissions();
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    }
  };

  const deleteSubmission = async (id) => {
    try {
      await api.delete(`/ppt-submissions/${id}`);
      await loadSubmissions();
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    }
  };

  const tabs = [
    { id: 'create', label: 'Create' },
    { id: 'submissions', label: `Submissions (${submissions.length})` },
    { id: 'platforms', label: 'Platforms' },
  ];

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
          <Presentation className="w-5 h-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">PPT Submissions</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Submit presentations to SlideShare, SpeakerDeck, Scribd and other platforms for off-page SEO.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Submissions', value: submissions.length },
          { label: 'Live', value: submissions.filter((s) => s.overall_status === 'live').length },
          { label: 'Partial', value: submissions.filter((s) => s.overall_status === 'partial').length },
          { label: 'Pending', value: submissions.filter((s) => s.overall_status === 'pending').length },
        ].map((stat) => (
          <div key={stat.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
            <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">{stat.value}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`pb-2 px-1 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-b-2 border-violet-600 text-violet-600'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        {/* CREATE TAB */}
        {activeTab === 'create' && (
          <form onSubmit={handleCreate} className="space-y-5 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Presentation Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="10 SEO Tips for Local Businesses in 2026"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A comprehensive guide covering local SEO strategies, citation building, and Google Business Profile optimization..."
                rows={3}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-none"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  File / Google Slides URL
                </label>
                <input
                  type="url"
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                  placeholder="https://drive.google.com/..."
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Website URL (for backlink)
                </label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yoursite.com"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Target Keywords <span className="text-xs text-slate-400">(comma separated)</span>
              </label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="local seo, google business profile, citation building"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Target Platforms <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {PLATFORM_OPTIONS.map((p) => (
                  <label
                    key={p.id}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedPlatforms.includes(p.id)
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes(p.id)}
                      onChange={() => togglePlatform(p.id)}
                      className="accent-violet-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{p.label}</p>
                      <p className="text-xs text-slate-400">{p.traffic} traffic</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-violet-600 text-white px-5 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              {loading ? 'Creating...' : 'Create Submission'}
            </button>
          </form>
        )}

        {/* SUBMISSIONS TAB */}
        {activeTab === 'submissions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">{submissions.length} total submissions</p>
              <button
                type="button"
                onClick={loadSubmissions}
                className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {submissions.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Presentation className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No submissions yet. Create your first one.</p>
              </div>
            ) : (
              submissions.map((sub) => (
                <div
                  key={sub.id}
                  className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{sub.title}</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{sub.description}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[sub.overall_status] || STATUS_STYLES.pending}`}>
                        {sub.overall_status}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteSubmission(sub.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {sub.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {sub.keywords.map((kw) => (
                        <span key={kw} className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {sub.platforms?.map((pt) => (
                      <div key={pt.platform} className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 rounded-md p-2">
                        <div>
                          <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{pt.name}</p>
                          <span className={`text-xs ${STATUS_STYLES[pt.status] || STATUS_STYLES.pending} px-1.5 py-0.5 rounded capitalize`}>
                            {pt.status}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {pt.status === 'pending' && (
                            <button
                              type="button"
                              title="Mark Submitted"
                              onClick={() => markPlatform(sub.id, pt.platform, 'submitted')}
                              className="text-blue-400 hover:text-blue-600"
                            >
                              <Clock className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {pt.status === 'submitted' && (
                            <button
                              type="button"
                              title="Mark Live"
                              onClick={() => markPlatform(sub.id, pt.platform, 'live')}
                              className="text-green-400 hover:text-green-600"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {pt.submit_url && (
                            <a
                              href={pt.submit_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-violet-500"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {sub.seo_benefits && (
                    <div className="flex gap-4 text-xs text-slate-500 dark:text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-800">
                      <span>🔗 {sub.seo_benefits.backlinks_expected} backlinks expected</span>
                      <span>👁 ~{sub.seo_benefits.estimated_monthly_views.toLocaleString()} monthly views</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* PLATFORMS TAB */}
        {activeTab === 'platforms' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              All supported presentation sharing platforms for off-page SEO.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {platforms.map((p) => (
                <div key={p.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{p.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{p.category}</p>
                    <div className="flex gap-3 mt-2 text-xs text-slate-500">
                      <span>Traffic: <strong className="text-slate-700 dark:text-slate-300">{p.estimated_traffic}</strong></span>
                      <span>DoFollow: <strong className="text-slate-700 dark:text-slate-300">{p.do_follow ? 'Yes' : 'No'}</strong></span>
                    </div>
                  </div>
                  <a
                    href={p.submit_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium shrink-0"
                  >
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {status && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            status.startsWith('Error')
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'
          }`}>
            {status}
          </div>
        )}
      </div>
    </motion.div>
  );
}
